import type { SxProps } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { getBillingStatusInfo } from '../../../../services/BillingUtils'

export type CompanyDialogPaletteTone = 'success' | 'warning' | 'error' | 'info' | 'secondary'

type PaletteToneKey = CompanyDialogPaletteTone

function chipFillFromPalette(theme: Theme, key: PaletteToneKey) {
  const main =
    key === 'secondary' ? theme.palette.secondary.main : theme.palette[key].main
  return {
    bg: alpha(main, 0.2),
    fg: main,
    border: alpha(main, 0.35),
  }
}

function chipSxFromTone(theme: Theme, key: PaletteToneKey): SxProps<Theme> {
  const t = chipFillFromPalette(theme, key)
  return {
    backgroundColor: t.bg,
    color: t.fg,
    fontWeight: 500,
    border: `1px solid ${t.border}`,
  }
}

/** Tone chip for billing / company surfaces (palette-driven, dark-mode safe). */
export function paletteToneChipSx(theme: Theme, key: CompanyDialogPaletteTone): SxProps<Theme> {
  return chipSxFromTone(theme, key)
}

function neutralMutedChipSx(theme: Theme): SxProps<Theme> {
  const g = theme.palette.text.disabled
  return {
    backgroundColor: alpha(g, 0.2),
    color: 'text.disabled',
    fontWeight: 500,
    border: `1px solid ${alpha(g, 0.35)}`,
  }
}

/** Billing account / Stripe subscription status — uses [[BillingUtils]] severity → palette. */
export function subscriptionStatusChipSx(theme: Theme, status: string): SxProps<Theme> {
  const { severity } = getBillingStatusInfo(status)
  return chipSxFromTone(theme, severity)
}

/** Payment status row + free-tier chip on the billing tab. */
export function paymentStatusDisplayChipSx(
  theme: Theme,
  paymentStatus: string | undefined | null,
  isFreeTier: boolean,
): SxProps<Theme> {
  if (isFreeTier) {
    return chipSxFromTone(theme, 'success')
  }
  const ps = (paymentStatus || '').toLowerCase()
  if (ps === 'trial' || ps === 'trialing') return chipSxFromTone(theme, 'info')
  if (ps === 'paid') return chipSxFromTone(theme, 'success')
  if (ps === 'overdue' || ps === 'failed') return chipSxFromTone(theme, 'error')
  if (ps === 'pending') return chipSxFromTone(theme, 'warning')
  return neutralMutedChipSx(theme)
}

export function trialUrgencyChipSx(theme: Theme, diffDays: number): SxProps<Theme> {
  if (diffDays < 0) return chipSxFromTone(theme, 'error')
  if (diffDays <= 3) return chipSxFromTone(theme, 'warning')
  return chipSxFromTone(theme, 'success')
}

export function trialUrgencyAccentColor(theme: Theme, diffDays: number): string {
  if (diffDays < 0) return theme.palette.error.main
  if (diffDays <= 3) return theme.palette.warning.main
  return theme.palette.success.main
}

/** Trial panel chip when status is raw lowercase (`active`, `trial`, …). */
export function rawSubscriptionAccountStatusChipSx(
  theme: Theme,
  status: string,
): SxProps<Theme> {
  const s = status.toLowerCase()
  if (s === 'active') return chipSxFromTone(theme, 'success')
  if (s === 'trial' || s === 'trialing') return chipSxFromTone(theme, 'info')
  if (s === 'cancelled') return chipSxFromTone(theme, 'error')
  if (s === 'past_due') return chipSxFromTone(theme, 'warning')
  return neutralMutedChipSx(theme)
}

export function subscriptionHistoryEventChipSx(
  theme: Theme,
  eventType: string,
): SxProps<Theme> {
  switch (eventType) {
    case 'trial_started':
      return chipSxFromTone(theme, 'info')
    case 'trial_ended':
      return chipSxFromTone(theme, 'error')
    case 'subscription_created':
    case 'payment':
      return chipSxFromTone(theme, 'success')
    case 'invoice':
      return chipSxFromTone(theme, 'warning')
    case 'billing_period_started':
      return chipSxFromTone(theme, 'secondary')
    default:
      return neutralMutedChipSx(theme)
  }
}

export function companyDialogInsetCardSx(): SxProps<Theme> {
  return {
    p: 2.5,
    bgcolor: 'action.hover',
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    height: '100%',
    backgroundImage: 'none',
  }
}

export function companyDialogNestedPaperSx(): SxProps<Theme> {
  return {
    p: 2,
    bgcolor: 'background.paper',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundImage: 'none',
  }
}

export function subscriptionHistoryRowPaperSx(theme: Theme): SxProps<Theme> {
  const primaryMain = theme.palette.primary.main
  return {
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'action.hover',
    backgroundImage: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'background.paper',
      borderColor: alpha(primaryMain, 0.35),
      boxShadow: theme.shadows[4],
    },
  }
}
